import { StorefrontError } from '../core/error'
import { useCustomerStore } from '@shared/global-store/stores/customer/customer'
import { generateId, isClientData } from '@shared/shop-api/core/helper'
import { execute } from '@shared/shop-api/customer/execute-graphql'
import { toWishlist } from '@shared/shop-api/wishlist/mapper'
import {
  customerWishlistQuery,
  setWishlistQuery,
} from '@shared/shop-api/wishlist/query'
import type { Wishlist, WishlistsInput } from '@shared/shop-api/wishlist/types'

export const getCustomerWishlists = async () => {
  const wishlistsData = await execute(customerWishlistQuery)

  const wishlists = wishlistsData?.customer?.metafield?.value

  if (!wishlists) {
    return []
  }

  try {
    return JSON.parse(wishlists).map(toWishlist) as Wishlist[]
  } catch (error) {
    throw new StorefrontError('Parsing wishlist data failed', error)
  }
}

export const setCustomerWishlists = async (
  wishlistsInput: WishlistsInput[]
) => {
  const { customer } = useCustomerStore()

  if (!customer?.id) {
    console.error('No customer id found')

    return
  }

  const wishlistData = await execute(setWishlistQuery, {
    customerId: customer?.id,
    wishlists: JSON.stringify(wishlistsInput),
  })

  if (
    !isClientData(wishlistData) ||
    !wishlistData?.metafieldsSet?.wishlists?.[0]?.value
  ) {
    throw new StorefrontError(
      'Setting new wishlist values failed',
      wishlistData
    )
  }

  try {
    const newWishlists = wishlistData?.metafieldsSet?.wishlists?.[0].value
      ? JSON.parse(wishlistData?.metafieldsSet?.wishlists[0]?.value)?.map(
          toWishlist
        )
      : []

    return newWishlists as Wishlist[]
  } catch (error) {
    throw new StorefrontError('Parsing wishlist data failed', error)
  }
}

export const addWishlistToCustomer = async () => {
  const wishlists = await getCustomerWishlists()

  const id = generateId('wishlist_')

  wishlists.push({
    id,
    name: `Wishlist ${wishlists.length + 1}`,
    wishlistItems: [],
    createdAt: Date.now(),
  })

  const updatedWishlists = await setCustomerWishlists(wishlists)

  const wishlist = updatedWishlists?.find((wishlist) => wishlist.id === id)

  return wishlist
}

export const getWishlistById = async (id: string) => {
  const wishlists = await getCustomerWishlists()

  return wishlists.find((wishlist) => wishlist.id === id)
}

export const addWishlistItem = async (
  wishlistId: string,
  productId: string
) => {
  const wishlists = await getCustomerWishlists()
  const wishlistIndex = wishlists.findIndex(
    (wishlist) => wishlist.id === wishlistId
  )

  if (wishlistIndex === -1) {
    throw new StorefrontError(
      'Wishlist with given Id not found for item addition',
      wishlists
    )
  }

  if (
    !wishlists[wishlistIndex].wishlistItems?.some(
      (item) => item.id === productId
    )
  ) {
    wishlists[wishlistIndex].wishlistItems?.push({ id: productId })
  }

  const updatedWishlists = await setCustomerWishlists(wishlists)

  return updatedWishlists?.find((wishlist) => wishlist.id === wishlistId)
}

export const deleteWishlistItem = async (
  wishlistId: string,
  wishlistItemId: string
) => {
  const wishlists = await getCustomerWishlists()
  const wishlistIndex = wishlists.findIndex(
    (wishlist) => wishlist.id === wishlistId
  )

  if (wishlistIndex === -1) {
    throw new StorefrontError(
      'Wishlist with given Id not found for item removal',
      wishlists
    )
  }

  wishlists[wishlistIndex].wishlistItems = wishlists[
    wishlistIndex
  ].wishlistItems?.filter((item) => item.id !== wishlistItemId)

  const updatedWishlists = await setCustomerWishlists(wishlists)

  return updatedWishlists?.find((wishlist) => wishlist.id === wishlistId)
}
